// import { IFile } from '../File/IFile'

import type { IContentFile } from 'Model/File/IContentFile.js'
import type { IPipelineInformation } from './IPipelineInformation.js'
import type { ISampleMetaData } from './ISampleMetaData.js'
import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.js'

export type EItemType = 'SAMPLE' | 'NEGATIVE_CONTROL'

export type SampleOrNegativeControl = {
  itemType: EItemType
}

export type Sample = FileForProcessing & {
  itemType: 'SAMPLE'
}

export function isSample(a: SampleOrNegativeControl): a is Sample {
  return a.itemType === 'SAMPLE'
}

export function isNegativeControl(a: SampleOrNegativeControl): a is NegativeControl {
  return a.itemType === 'NEGATIVE_CONTROL'
}

export type NegativeControl = FileForProcessing & {
  itemType: 'NEGATIVE_CONTROL'
}

/* warning: geojson defines its coordinates order as lon, lat - as opposed to all other formats known to mankind */
export type ICoordinates = {
  type: 'Point'
  coordinates: [number, number]
}

export const geoJson2Normal: (coordinates: [number, number]) => [number, number] = (coordinates: [number, number]) => [
  coordinates[1],
  coordinates[0]
]

export type FileForProcessing = SampleOrNegativeControl & {
  id: string
  forwardFile?: IContentFile
  forwardFileId: string
  forwardPrimer?: string
  reverseFile?: IContentFile
  version: string | null
  reverseFileId?: string
  reversePrimer?: string
  customerSampleName?: string
  poolLabel?: string
  primerLiterature?: string
  minBasePairLength?: number
  maxBasePairLength?: number
  pipelineInformation?: IPipelineInformation
  otuCount?: number
  orderId?: string
  poolId?: string
  sampleMetadata?: ISampleMetaData
  isTrimmed: boolean
  isMerged: boolean
  /** Derived from the submitted reads, not declared — unlike `sampleMetadata.sequencer`, which the customer types. */
  detectedSequencingPlatform?: SequencingPlatformKey
}

export type FileInProcessing = FileForProcessing &
  Required<
    Pick<
      FileForProcessing,
      | 'pipelineInformation'
      | 'forwardFile'
      | 'reverseFile'
      | 'forwardFileId'
      | 'reverseFileId'
      | 'forwardPrimer'
      | 'reversePrimer'
      | 'poolId'
      | 'orderId'
      | 'customerSampleName'
      | 'sampleMetadata'
    >
  >
export function isFileInProcessing(a: FileForProcessing): a is FileInProcessing {
  return (
    !!a.pipelineInformation && !!a.forwardFile && (!!a.reverseFile || a.isMerged) && !!a.forwardFileId && (!!a.reverseFileId || a.isMerged)
  )
}

export function isSampleIncomplete(a: FileForProcessing) {
  return !isSampleReadyForSubmission(a)
}

type NonNullableAndRequiredFields<T, K extends keyof T> = {
  [P in keyof T]: P extends K ? NonNullable<T[P]> : T[P]
} & {
  [P in K]-?: NonNullable<T[P]>
}

export type SampleForSubmission = NonNullableAndRequiredFields<FileForProcessing, 'forwardPrimer' | 'reversePrimer' | 'customerSampleName'>

export function isSampleReadyForSubmission(a: FileForProcessing): a is SampleForSubmission {
  if (isSample(a)) {
    if (a.isTrimmed) {
      return !!a.customerSampleName
    }
    return !!a.forwardPrimer && !!a.reversePrimer && !!a.customerSampleName
  } else return a.isTrimmed || (!!a.forwardPrimer && !!a.reversePrimer)
}
